import { useEffect } from 'react';
import L from 'leaflet';
import { useAqua } from '../lib/aquaStore';
import { pColor, tColor } from '../lib/aquaNet';

const n1 = (v, d = 1) => (v == null || !Number.isFinite(v) ? '–' : v.toFixed(d).replace('.', ','));
const vColor = (v) => (v < 0.005 ? '#94a3b8' : v < 0.1 ? '#7c3aed' : v < 0.5 ? '#2563eb' : v < 1.5 ? '#16a34a' : v < 2.5 ? '#f97316' : '#dc2626');
const aColor = (h) => (!Number.isFinite(h) ? '#7c3aed' : h < 6 ? '#16a34a' : h < 24 ? '#eab308' : h < 72 ? '#f97316' : '#dc2626');

// colours a link by the chosen result (pressure, temperature, water age, velocity)
// roughness map: smooth new pipes green, incrusted old pipes red
export const KCOL = [[0.1, '#16a34a'], [0.5, '#84cc16'], [1.5, '#eab308'], [3, '#f97316'], [Infinity, '#dc2626']];
const kColor = (k) => (!Number.isFinite(k) ? '#94a3b8' : KCOL.find(([x]) => k <= x)[1]);

export function linkColor(s, l) {
  if (l.dead) return '#64748b';
  const n = s.res.nodes, pa = n[l.a]?.bar, pb = n[l.b]?.bar;
  if (s.mode === 't' && s.th) return tColor(s.th.links[l.id]?.tOut);
  if (s.mode === 'age' && s.eps) return aColor(Math.max(s.eps.age[l.a] ?? 0, s.eps.age[l.b] ?? 0));
  if (s.mode === 'age' && s.th) return aColor(Math.max(s.th.age[l.a] ?? 0, s.th.age[l.b] ?? 0) / 3600);
  if (s.mode === 'v') return vColor(Math.abs(l.v));
  if (s.mode === 'k') return kColor(l.k);
  return pColor(pa != null && pb != null ? Math.min(pa, pb) : pa ?? pb);
}

// 2D map layer: coloured pipes, flow arrows, unsupplied objects
export default function useAquaLayer(mapRef, t) {
  const s = useAqua();
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !s.on || !s.res || s.res.error) return undefined;
    if (!map.getPane('aqua')) { map.createPane('aqua'); map.getPane('aqua').style.zIndex = 470; }
    const g = L.layerGroup().addTo(map), z = map.getZoom();
    s.res.links.filter((l) => l.kind === 'pipe').forEach((l) => {
      const th = s.th?.links[l.id];
      L.polyline(l.coords, { pane: 'aqua', color: linkColor(s, l), weight: 8, opacity: 0.85, dashArray: l.dead ? '6 8' : null, interactive: true })
        .bindTooltip(l.dead ? t.aqDead : `DN ${Math.round(l.d)} · k ${n1(l.k, 2)} mm · Q ${n1(Math.abs(l.q), 2)} l/s · v ${n1(Math.abs(l.v), 2)} m/s · ${n1(s.res.nodes[l.a]?.bar)}→${n1(s.res.nodes[l.b]?.bar)} bar${th ? ` · ${n1(th.tOut)} °C${th.stag ? ` (${t.aqStag})` : ''}` : ''}`, { sticky: true }).addTo(g);
      if (l.dead || Math.abs(l.q) < 0.001 || l.len < 2) return;
      const k = Math.floor(l.coords.length / 2), a = l.coords[Math.max(0, k - 1)], b = l.coords[Math.min(l.coords.length - 1, k)];
      const pa = map.project(a, z), pb = map.project(b, z), ang = (Math.atan2(pb.y - pa.y, pb.x - pa.x) * 180) / Math.PI + (l.q < 0 ? 180 : 0);
      const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      L.marker(mid, { pane: 'aqua', interactive: false, icon: L.divIcon({ className: 'aq-arrow-wrap', iconSize: [18, 18], iconAnchor: [9, 9], html: `<i class="aq-arrow" style="transform:rotate(${ang}deg)">➤</i>` }) }).addTo(g);
    });
    (s.res.draws || []).forEach((d) => { const nd = s.res.nodes[d.fid]; if (!nd) return; const low = d.got < d.want * 0.95; L.marker([nd.lat, nd.lon], { pane: 'aqua', zIndexOffset: 1200, interactive: false, icon: L.divIcon({ className: 'aq-draw-wrap', iconSize: [0, 0], html: `<span class="aq-draw-pin ${low ? 'low' : ''}" data-testid="aq-draw-pin"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8 8 5 11.5 5 15a7 7 0 0 0 14 0c0-3.5-3-7-7-13z"/></svg>${d.fire ? 'LW ' : ''}${n1(d.got, 1)}${low ? ` / ${n1(d.want, 1)}` : ''} l/s</span>` }) }).addTo(g); });
    s.res.dry.forEach((ll) => L.circleMarker(ll, { pane: 'aqua', radius: 13, color: '#dc2626', weight: 3, fillOpacity: 0.15, dashArray: '4 4', interactive: false }).addTo(g));
    return () => g.remove();
  }, [mapRef, s, t]);
  // leak search: likely pipe sections yellow -> red, the most likely ones on top
  useEffect(() => {
    const map = mapRef.current, ls = s.ls;
    if (!map || !ls?.links || ls.none) return undefined;
    if (!map.getPane('aqls')) { map.createPane('aqls'); map.getPane('aqls').style.zIndex = 475; }
    const g = L.layerGroup().addTo(map);
    [...ls.links].filter((l) => l.rel >= 0.1).sort((a, b) => a.rel - b.rel).forEach((l) => {
      const c = l.rel > 0.66 ? '#dc2626' : l.rel > 0.33 ? '#f97316' : '#facc15';
      L.polyline(l.coords, { pane: 'aqls', color: c, weight: 5 + 7 * l.rel, opacity: 0.9, className: 'lf-line' }).bindTooltip(`${t.lfProb}: ${n1(l.prob * 100, 1)} %`, { sticky: true }).addTo(g);
    });
    return () => g.remove();
  }, [mapRef, s.ls, t]);
  // suggested measuring hydrants: numbered purple pins
  useEffect(() => {
    const map = mapRef.current, pts = s.mp?.pts;
    if (!map || !pts?.length) return undefined;
    const g = L.layerGroup().addTo(map);
    pts.forEach((p, i) => L.marker([p.lat, p.lon], { zIndexOffset: 950, icon: L.divIcon({ className: 'pin-wrap', html: `<span class="mp-pin"><b>${i + 1}</b></span>`, iconSize: [26, 26], iconAnchor: [13, 30] }) }).bindTooltip(`${i + 1}. ${p.name || ''}`).addTo(g));
    return () => g.remove();
  }, [mapRef, s.mp]);
}
